import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Anchor, Lock } from "lucide-react";

export default function AcceptInvite() {
  const nav = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [state, setState] = useState({ loading: true, valid: false, email: "", error: "" });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/invitations/validate?token=${encodeURIComponent(token)}`);
        setState({ loading: false, valid: true, email: data.email, error: "" });
      } catch (e) {
        setState({ loading: false, valid: false, email: "", error: formatApiError(e.response?.data?.detail) || "Invalid link." });
      }
    })();
  }, [token]);

  const submit = async () => {
    if (pw.length < 8) return setState((s) => ({ ...s, error: "Password must be at least 8 characters." }));
    if (pw !== pw2) return setState((s) => ({ ...s, error: "Passwords do not match." }));
    setBusy(true);
    try {
      await api.post("/auth/invitations/accept", { token, password: pw });
      window.location.href = "/";
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e.response?.data?.detail) || "Could not accept invitation." }));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-deepnavy text-white px-4" data-testid="accept-invite-page">
      <div className="w-full max-w-md bg-white text-slate rounded-sm p-8 border border-border">
        <div className="flex items-center gap-2 mb-6"><Anchor className="h-7 w-7 text-navy" /><span className="font-serif text-2xl text-navy">ENDURANCE</span></div>
        {state.loading ? <p className="text-mutedsea">Validating your invitation…</p> : !state.valid ? (
          <div>
            <h1 className="font-serif text-xl text-navy mb-2">Invitation unavailable</h1>
            <p className="text-sm text-seared" data-testid="invite-error">{state.error}</p>
            <Button className="mt-4 bg-navy text-white" onClick={() => nav("/login")}>Go to sign in</Button>
          </div>
        ) : (
          <div>
            <h1 className="font-serif text-xl text-navy mb-1">Set your password</h1>
            <p className="text-sm text-mutedsea mb-4">Activating <b>{state.email}</b>. This link can be used once.</p>
            <label className="text-sm font-medium">New password</label>
            <Input type="password" data-testid="invite-password" value={pw} onChange={(e) => setPw(e.target.value)} className="mb-3 mt-1" placeholder="At least 8 characters" />
            <label className="text-sm font-medium">Confirm password</label>
            <Input type="password" data-testid="invite-password-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mb-3 mt-1" />
            {state.error && <p className="text-sm text-seared mb-2" data-testid="invite-error">{state.error}</p>}
            <Button data-testid="invite-submit" disabled={busy} onClick={submit} className="w-full bg-navy hover:bg-atlantic text-white">
              <Lock className="h-4 w-4 mr-1.5" /> {busy ? "Activating…" : "Activate account"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
